import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import common from '../../common.module.scss';
import styles from './schedule.module.css';
import { getSchedules, deleteSchedule } from '../../api/adminApi';
import ConfirmDeleteModal from '../ConfirmDeleteModal.jsx';
import { toast } from 'react-toastify';
import Pagination from '../pagination';
import { EmployeeContext } from '../../context/EmployeeContext.jsx';
import useIsMobile from '@/shared/lib/hooks/useIsMobile';
import { format } from 'date-fns';
import MobileBlocksSchedule from '../mobileBlockSchedule';

// добавлено:
import { GiSettingsKnobs } from "react-icons/gi";
import FilterModal from "@/shared/ui/FiltersModal";

export default function ScheduleTable() {
    const { employee } = useContext(EmployeeContext);
    const role = employee?.role;

    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [searchParams, setSearchParams] = useSearchParams();

    // начальные значения из query
    const [search,     setSearch]     = useState(searchParams.get('search')     ?? '');
    const [sortBy,     setSortBy]     = useState(searchParams.get('sortBy')     ?? '');
    const [direction,  setDirection]  = useState(searchParams.get('direction')  ?? 'asc');
    const [page,       setPage]       = useState(Number(searchParams.get('page') ?? 0));
    const [isEnding,   setIsEnding]   = useState(searchParams.get('isEnding') === 'true');

    const [games,      setGames]      = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [          , setLoading]    = useState(true);
    const [toDelete,   setToDelete]   = useState(null);

    // добавлено:
    const [showModalFilters, setShowModalFilters] = useState(false);

    const buildQuery = () =>
        '?' +
        new URLSearchParams({
            page:      String(page),
            search,
            sortBy,
            direction,
            isEnding:  String(isEnding),
        }).toString();

    // пишем изменения обратно в URL
    useEffect(() => {
        const p = new URLSearchParams();
        p.set('page', page);
        if (search)  p.set('search', search);
        if (sortBy) {
            p.set('sortBy', sortBy);
            p.set('direction', direction);
        }
        p.set('isEnding', isEnding);
        setSearchParams(p, { replace: true });
    }, [page, search, sortBy, direction, isEnding]);

    // загрузка данных
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await getSchedules({
                    page,
                    size: 10,
                    ...(search  && { search }),
                    ...(sortBy  && { sortBy, direction }),
                    isEnding,
                });
                const data = res.data.content || res.data;
                setGames(data);
                setTotalPages(res.data.totalPages || 1);
            } catch (e) {
                toast.error(e?.response?.data?.message || 'Ошибка при загрузке расписания');
            } finally {
                setLoading(false);
            }
        })();
    }, [page, search, sortBy, direction, isEnding]);

    const handleSort = (field) => {
        if (sortBy !== field) {
            setSortBy(field);
            setDirection('desc');
        } else if (direction === 'desc') {
            setDirection('asc');
        } else {
            setSortBy('');
            setDirection('asc');
        }
        setPage(0);
    };

    const renderSortArrow = (field) => {
        if (sortBy !== field) return '';
        return direction === 'asc' ? ' ↑' : ' ↓';
    };

    const handleDelete = async () => {
        try {
            await deleteSchedule(toDelete.id);
            setGames(prev => prev.filter(g => g.id !== toDelete.id));
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка удаления';
            toast.error(errorMessage);
            console.error('Ошибка удаления', e);
        } finally {
            setToDelete(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    const handleToDelete = (game) => setToDelete(game);

    // === сортировка через мобильную модалку ===
    const handleChangeSort = (field, dir) => {
        setSortBy(field);
        setDirection(dir);
        setPage(0);
    };

    // поля сортировки под бэкенд/хедеры
    const sortOptions = [
        { value: 'id',                   label: 'ID' },
        { value: 'startedAt',            label: 'Дата' },
        { value: 'title',                label: 'Название' },
        { value: 'gameMaster',           label: 'Мастер' },
        { value: 'currentCountPlayers',  label: 'Кол-во игроков' },
        { value: 'room',                 label: 'Зал' },
    ];

    return (
        <div className={`container-fluid ${common.container}`}>
            {isMobile && (<h1 className={common.mobileHeading}>Расписание</h1>)}

            <div className="d-flex align-items-center mb-3">
                <div className="col-md-6 col-10">
                    <input
                        type="text"
                        placeholder="Поиск по названию"
                        className={`form-control ${common.searchInput}`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>

                {/* кнопка сортировки на мобилке */}
                {isMobile && (
                    <div className="col-2 d-flex justify-content-end">
                        <button
                            type="button"
                            className={`${common.iconButton} ms-2`}
                            onClick={() => setShowModalFilters(true)}
                            aria-label="Открыть сортировку"
                        >
                            <GiSettingsKnobs size={20} />
                        </button>
                    </div>
                )}

                {role !== "ADMIN" && role !== "MASTER" && !isMobile && (
                    <button
                        className="service-btn col-auto ms-3"
                        onClick={() => navigate(`/admin/schedule/create${buildQuery()}`)}
                    >
                        Добавить игру
                    </button>
                )}
            </div>

            <div>
                <button
                    className="service-btn mb-4"
                    onClick={() => {
                        setPage(0);
                        setIsEnding(!isEnding);
                    }}
                >
                    {isEnding ? "Скрыть прошедшие игры" : "Показать прошедшие игры"}
                </button>
            </div>

            {!isMobile ? (
                <table className={`table ${common.table}`}>
                    <thead className={common.tableHeader}>
                    <tr>
                        <th className={common.sortable} onClick={() => handleSort('id')}>
                            ID{renderSortArrow('id')}
                        </th>
                        <th className={common.sortable}>
                            ID игры
                        </th>
                        <th className={common.sortable} onClick={() => handleSort('startedAt')}>
                            Дата{renderSortArrow('startedAt')}
                        </th>
                        <th className={common.sortable} onClick={() => handleSort('title')}>
                            Название{renderSortArrow('title')}
                        </th>
                        <th className={common.sortable} onClick={() => handleSort('gameMaster')}>
                            Мастер{renderSortArrow('gameMaster')}
                        </th>
                        <th className={common.sortable} onClick={() => handleSort('currentCountPlayers')}>
                            Кол-во игроков{renderSortArrow('currentCountPlayers')}
                        </th>
                        <th className={common.sortable} onClick={() => handleSort('room')}>
                            Зал{renderSortArrow('room')}
                        </th>
                        {role !== "MASTER" && (
                            <>
                                <th></th>
                                {role !== "ADMIN" && <th></th>}
                            </>
                        )}
                    </tr>
                    </thead>
                    <tbody>
                    {games.map((game, index) => {
                        const isEnded = game.isEnding;
                        return (
                            <tr key={game.id} className={`${index % 2 === 1 ? common.altRow : ''} ${isEnded && common.ended}`}>
                                <td>{game.id}</td>
                                <td>{game.gameId}</td>
                                <td>
                                    {format(new Date(game.startedAt), 'dd.MM.yyyy')}<br />
                                    <span className={styles.time}>
                      в {new Date(game.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                                </td>
                                <td>{game.title}</td>
                                <td>{game.gameMaster}</td>
                                <td>{game.currentCountPlayers} / {game.maxCountPlayers}</td>
                                <td>{game.room}</td>
                                <td>
                    <span
                        className={`btn-word black btn-link p-0 ${common.editBtn}`}
                        onClick={() => navigate(`/admin/schedule/edit/${game.id}${buildQuery()}`)}
                    >
                      Редактировать
                    </span>
                                </td>
                                {role !== "ADMIN" && role !== "MASTER" && (
                                    <td>
                      <span
                          className="btn-word black btn-link text-danger p-0"
                          onClick={() => setToDelete(game)}
                      >
                        Удалить
                      </span>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            ) : (
                <MobileBlocksSchedule games={games} setToDelete={handleToDelete} />
            )}

            <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />

            {role !== "ADMIN" && role !== "MASTER" && isMobile && (
                <button
                    className="service-btn col-auto w-100 mt-4"
                    onClick={() => navigate(`/admin/schedule/create${buildQuery()}`)}
                >
                    Добавить игру
                </button>
            )}

            {toDelete && (
                <ConfirmDeleteModal
                    roomName={toDelete.title}
                    onConfirm={handleDelete}
                    onCancel={() => setToDelete(null)}
                />
            )}

            {/* модалка сортировки на мобилке */}
            <FilterModal
                isOpen={showModalFilters}
                onClose={() => setShowModalFilters(false)}
                sortOptions={sortOptions}
                sortBy={sortBy}
                direction={direction}
                onChangeSort={handleChangeSort}
            />
        </div>
    );
}
